import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from "../../constants";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, MenuItem, Snackbar, TextField, Typography } from '@mui/material';
import { parseWipDuration, formatWipDuration } from '../../utils/wipDuration';
import useCheckToken from '../../hooks/useCheckToken';
import useExchangeToken from '../../hooks/useExchangeToken';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);
const showSavePopup = (options) => MySwal.fire({
  confirmButtonText: 'Ok,Got it!',
  didOpen: () => {
    const button = document.querySelector('.swal2-confirm');
    if (button) {
      button.style.fontFamily = 'CPF_Imm_Sook';
      button.style.fontSize = '20px';
    }
  },
  ...options,
});

export const WIP_FIELDS = [
  ['product_group', 'Product Group'], ['product_code', 'Product Code'],
  ['product_name', 'Product Name'], ['zone', 'Zone'],
  ['product_type', 'Product Type'], ['ingredient', 'Ingredient'],
  ['process_step_full', 'Process Step Full'], ['process_step', 'Process Step'],
  ['time_step', 'Time Step'], ['time_step_unit', 'Time Step Unit'],
  ['use_within', 'Use Within'], ['use_within_unit', 'Use Within Unit'],
  ['shelf_life', 'Shelf Life'], ['shelf_life_unit', 'Shelf Life Unit'],
  ['storage_unit', 'Storage Unit'],
];
const NUMERIC_FIELDS = ['time_step', 'use_within', 'shelf_life'];
const empty = Object.fromEntries(WIP_FIELDS.map(([key]) => [key, '']));
NUMERIC_FIELDS.forEach(key => { empty[key] = '00:00:00'; empty[`${key}_unit`] = 'sec'; });
const choices = { zone: ['ดิบ', 'สุก'], product_type: ['Finish Good', 'Raw Material', 'Work In Process', 'Other'], storage_unit: ['KG', 'PC', 'Batch', 'ถัง', 'ฟอง'] };
const help = {
  product_group: 'กลุ่มสินค้า เช่น ไก่ปรุงสุก', product_code: 'รหัสสินค้า เช่น 23111277 (ไม่ตรวจสอบกับ SAP)',
  product_name: 'ชื่อสินค้าที่แสดงในหน้า Register และ Sticker', zone: 'เลือกโซนจัดเก็บ ดิบ หรือ สุก',
  product_type: 'เลือก Finish Good (สินค้าสำเร็จรูป), Raw Material (วัตถุดิบ), Work In Process (ระหว่างผลิต) หรือ Other (อื่น ๆ)', ingredient: 'วัตถุดิบบน Sticker เช่น น้ำจิ้มแจ่ว (สูงสุด 30 อักษรบน Sticker)',
  process_step_full: 'ชื่อขั้นตอนเต็ม เช่น แช่เย็นหลังปรุงสุก', process_step: 'ชื่อขั้นตอนที่ใช้เลือกตอน Register เช่น รอแช่เย็น',
  time_step: 'ระยะเวลาขั้นตอน เช่น 00:15:00 = 15 นาที ใช้คำนวณ End Time',
  use_within: 'นับจาก End Time ถึงเริ่มเตือน Must Use เช่น 01:00:00',
  shelf_life: 'นับจาก End Time ถึงหมดอายุ เช่น 02:00:00 ต้องไม่น้อยกว่า Use Within',
  storage_unit: 'หน่วยของจำนวน เช่น ถัง',
};
const required = ['product_group', 'product_code', 'product_name', 'zone',
  'product_type', 'ingredient', 'process_step_full', 'process_step',
  'time_step', 'time_step_unit', 'use_within', 'use_within_unit',
  'shelf_life', 'shelf_life_unit', 'storage_unit'];

export default function WipMasterForm({ edit = false }) {
  useCheckToken();
  const exchangeToken = useExchangeToken();
  const auth = useSelector(state => state.auth);
  const token = auth?.access_token;
  const loggedInEmail = String(auth?.email ?? '').trim();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const listPath = params.plant ? location.pathname.replace(/\/(new|edit)\/?$/, '') : '/list-master-std';
  const listQuery = new URLSearchParams(location.search);
  listQuery.delete('id');
  const backPath = listPath + (listQuery.toString() ? `?${listQuery.toString()}` : '');
  const plant = params.plant || sessionStorage.getItem('master_std_plant');
  const factory = params.factory || sessionStorage.getItem('master_std_factory');
  const id = new URLSearchParams(window.location.search).get('id');
  const [values, setValues] = useState({ ...empty, plant_id: plant || '', factory: factory || '', line: '', weight: false, qty: false });
  const [busy, setBusy] = useState(edit);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (!edit) return;
    if (!id || !/^\d+$/.test(id)) { setError('Master ID ไม่ถูกต้อง'); setBusy(false); return; }
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const result = await axios.get(apiUrl.WIP_TRACKING, {
          params: { plant_id: plant, factory },
          headers: { Authorization: `Bearer ${token}` },
        });
        const row = (Array.isArray(result.data) ? result.data : []).find(item => String(item.id) === id);
        if (!row) throw new Error('ไม่พบข้อมูล Master ID นี้');
        if (active) {
          setValues({ ...empty, ...row, ...Object.fromEntries(NUMERIC_FIELDS.map(key => [key, formatWipDuration(row[key], row[`${key}_unit`])])) });
          setBusy(false);
        }
      } catch (err) {
        if (active) { setError(err.response?.data?.detail || err.message); setBusy(false); }
      }
    })();
    return () => { active = false; };
  }, [edit, id, token, plant, factory]);

  const change = (key, value) => setValues(prev => ({ ...prev, [key]: value }));
  const submit = async event => {
    event.preventDefault();
    setError('');
    if (!token || !plant || !factory || (edit && !id)) { setError('ข้อมูล plant, factory หรือ token ไม่พร้อม'); return; }
    if (required.filter(key => !key.endsWith('_unit') || key === 'storage_unit').some(key => !String(values[key] ?? '').trim())) { setError('กรอกข้อมูลที่จำเป็นให้ครบ'); return; }
    if (NUMERIC_FIELDS.some(key => parseWipDuration(values[key]) === null)) {
      setError('กรอกเวลาเป็น HH:MM:SS เช่น 00:15:00'); return;
    }
    if (parseWipDuration(values.use_within) > parseWipDuration(values.shelf_life)) { setError('Use Within ต้องไม่เกิน Shelf Life'); return; }
    if (!edit && !loggedInEmail) { setError('ไม่พบ email ของผู้ที่ล็อกอิน กรุณาตรวจสอบ state.auth.email'); return; }
    const body = {
      plant_id: plant, factory,
      ...Object.fromEntries(WIP_FIELDS.map(([key]) => [key,
        NUMERIC_FIELDS.includes(key) ? parseWipDuration(values[key]) : key.endsWith('_unit') && key !== 'storage_unit' ? 'sec' : String(values[key] ?? '').trim()])),
      weight: Boolean(values.weight), qty: Boolean(values.qty),
      ...(!edit ? { created_by: loggedInEmail } : {}),
    };
    setBusy(true);
    try {
      const freshToken = await exchangeToken();
      const config = { headers: { Authorization: `Bearer ${freshToken || token}` } };
      if (edit) await axios.put(`${apiUrl.WIP_TRACKING}${id}/`, body, config);
      else await axios.post(apiUrl.WIP_TRACKING, body, config);
      if (edit) {
        setSaveSuccess(true);
        setTimeout(() => navigate(backPath, { replace: true }), 2000);
      } else {
        await showSavePopup({
          title: 'Good job!',
          text: 'Successfully created new master std!',
          icon: 'success',
        });
        navigate(backPath, { replace: true });
      }
    } catch (err) {
      const message = err.response?.data?.detail || JSON.stringify(err.response?.data || err.message);
      setError(message);
      if (edit) {
        setSaveFailed(true);
      } else {
        await showSavePopup({ title: 'Failed!', text: message, icon: 'error' });
      }
    } finally { setBusy(false); }
  };

  return <Box component="form" onSubmit={submit} sx={{ p: 3, bgcolor: "#fff", minHeight: "100vh", boxSizing: "border-box" }}>
    {edit && <>
      <Snackbar open={saveSuccess} autoHideDuration={4000} onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSaveSuccess(false)} sx={{ width: '100%' }}>
          The changes have been saved successfully.
        </Alert>
      </Snackbar>
      <Snackbar open={saveFailed} autoHideDuration={4000} onClose={() => setSaveFailed(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="error" onClose={() => setSaveFailed(false)} sx={{ width: '100%' }}>
          Failed to save changes.
        </Alert>
      </Snackbar>
    </>}
    <Typography variant="h4" sx={{ mb: 2 }}>{edit ? 'Edit WIP Master' : 'New WIP Master'}</Typography>
    <Alert severity="info" sx={{ mb: 3 }}>Master ใช้ร่วมกันทั้งโรงงาน ไม่แยก Line และไม่ตรวจสอบ SAP — เวลาทั้งหมดเป็นระยะเวลา HH:MM:SS โดย Use Within เริ่มเตือน และ Shelf Life คือหมดอายุหลัง End Time</Alert>
    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
    <Grid container spacing={2}>
      {['plant_id', 'factory', ...WIP_FIELDS.map(([key]) => key).filter(key => !key.endsWith('_unit') || key === 'storage_unit')].map(key => {
        const label = WIP_FIELDS.find(([field]) => field === key)?.[1] || ({ plant_id: 'Plant', factory: 'Factory', line: 'Line' })[key];
        return <Grid item xs={12} md={6} key={key}>
          <TextField fullWidth label={label} value={values[key] ?? ''}
            select={Boolean(choices[key])}
            helperText={help[key] || ' '}
            placeholder={NUMERIC_FIELDS.includes(key) ? '00:00:00' : undefined}
            inputProps={NUMERIC_FIELDS.includes(key) ? { pattern: '[0-9]{2,}:[0-5][0-9]:[0-5][0-9]' } : undefined}
            disabled={busy || key === 'plant_id' || key === 'factory'}
            required={required.includes(key)}
            onChange={event => change(key, event.target.value)} >
            {choices[key] && [...new Set([...choices[key], ...(values[key] ? [values[key]] : [])])].map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
        </Grid>;
      })}
      {['weight', 'qty'].map(key => <Grid item xs={12} md={2} key={key}>
        <FormControlLabel label={key === 'weight' ? 'ให้กรอกน้ำหนัก (Weight)' : 'ให้กรอกจำนวน (Qty)'}
          control={<Checkbox disabled={busy} checked={Boolean(values[key])}
            onChange={event => change(key, event.target.checked)} />} />
      </Grid>)}
    </Grid>
    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
      <Button type="button" onClick={() => navigate(backPath)}>Back</Button>
      <Button type="submit" variant="contained" disabled={busy}>{busy ? <CircularProgress size={20} /> : 'Save'}</Button>
    </Box>
  </Box>;
}
